import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';

import { useTransferStock } from '../../hooks';
import type { ToastSeverity } from '../../lib/useToast';
import { useLocationLookup } from '../../lib/useLocationLookup';
import { LocationSelect } from './LocationSelect';

export interface TransferStockDialogProps {
  open: boolean;
  onClose: () => void;
  onResult: (severity: ToastSeverity, message: string) => void;
}

function toPositiveNumberOrUndefined(value: string): number | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  const n = Number(trimmed);
  return Number.isNaN(n) || n <= 0 ? undefined : n;
}

/**
 * Atomically transfers stock from one location to another (page-level action). The destination
 * must differ from the source — validated client-side too, mirroring the backend's own rejection
 * (`400` when source === destination).
 */
export function TransferStockDialog({ open, onClose, onResult }: TransferStockDialogProps) {
  const { t } = useTranslation('inventory');
  const transfer = useTransferStock();
  const { locations } = useLocationLookup();

  const [productId, setProductId] = useState('');
  const [sourceLocationId, setSourceLocationId] = useState('');
  const [destinationLocationId, setDestinationLocationId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) {
      setProductId('');
      setSourceLocationId('');
      setDestinationLocationId('');
      setQuantity('');
      setNote('');
    }
  }, [open]);

  const quantityValue = toPositiveNumberOrUndefined(quantity);
  const sameLocation =
    sourceLocationId !== '' &&
    destinationLocationId !== '' &&
    sourceLocationId === destinationLocationId;
  const valid =
    productId.trim() !== '' &&
    sourceLocationId !== '' &&
    destinationLocationId !== '' &&
    !sameLocation &&
    quantityValue !== undefined;

  const submit = () => {
    if (!valid || quantityValue === undefined) return;
    transfer.mutate(
      {
        productId: productId.trim(),
        sourceLocationId,
        destinationLocationId,
        quantity: quantityValue,
        note: note.trim() || undefined,
      },
      {
        onSuccess: () => {
          onResult('success', t('transferStockDialog.success'));
          onClose();
        },
        onError: (error) => onResult('error', error.message),
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('transferStockDialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            label={t('transferStockDialog.productIdLabel')}
            required
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            error={productId.trim() === ''}
            autoFocus
            fullWidth
            slotProps={{ htmlInput: { style: { fontFamily: 'JetBrains Mono, monospace' } } }}
          />
          <LocationSelect
            label={t('transferStockDialog.sourceLocationLabel')}
            value={sourceLocationId}
            onChange={setSourceLocationId}
            locations={locations}
            required
            error={sourceLocationId === ''}
            fullWidth
          />
          <LocationSelect
            label={t('transferStockDialog.destinationLocationLabel')}
            value={destinationLocationId}
            onChange={setDestinationLocationId}
            locations={locations}
            required
            error={destinationLocationId === '' || sameLocation}
            helperText={sameLocation ? t('transferStockDialog.sameLocationError') : undefined}
            fullWidth
          />
          <TextField
            label={t('transferStockDialog.quantityLabel')}
            type="number"
            required
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            error={quantity.trim() !== '' && quantityValue === undefined}
            fullWidth
            slotProps={{ htmlInput: { min: 0, step: 'any' } }}
          />
          <TextField
            label={t('transferStockDialog.noteLabel')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={transfer.isPending}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!valid || transfer.isPending}
          startIcon={
            transfer.isPending ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          {t('common:action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
